import React, { useEffect } from 'react';
import { Hyperlink } from '../../../components/atoms/Hyperlink';
import { SVG, Icons } from '../../../components/atoms/SVG';
import { CollapsibleContent } from '../../../components/organisms/CollapsibleOverlay/CollapsibleOverlay';
import useCollapsibleOverlay from '../../../hooks/useCollapsibleOverlay';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { isNotNullOrUndefined, isNullOrUndefined } from '../../../lib/typeGuards';
import { FilterButton } from '../../../components/molecules/ResultListSettings/partials/FilterButton';

export type ActiveFilterLink<Name extends string = string> = {
  name: Name;
  active?: boolean;
};
export type SelectableFilterLink<Name extends string = string> = {
  name: Name;
  url: string;
  active?: boolean;
};

export type FilterLink<Name extends string = string> = ActiveFilterLink<Name> | SelectableFilterLink<Name>;

export function isActiveLink<T extends string = string>(link: FilterLink<T>): link is ActiveFilterLink<T> {
  const selectableLink = link as SelectableFilterLink<T>;

  if ('active' in link) {
    return link.active === true;
  }

  return isNullOrUndefined(selectableLink?.url);
}

export function isSelectableLink<T extends string = string>(link: FilterLink<T>): link is SelectableFilterLink<T> {
  if ('active' in link) {
    return link.active === false;
  }

  return isNotNullOrUndefined((link as SelectableFilterLink<T>)?.url);
}

export type SelectLinks = {
  links: FilterLink[];
  headline: string;
};

export function SelectLinks({ headline, links }: SelectLinks) {
  const { t } = useSonTranslation('phrases');

  const { collapsibleProps, collapsibleOpen, toggleProps, popperInstance } = useCollapsibleOverlay('bottom');

  useEffect(() => {
    if (collapsibleOpen && popperInstance) {
      popperInstance.update();
    }
  });

  // no url property -> item is active
  const activeLink: any = links.find(isActiveLink);

  return (
    <div className="select-links">
      <FilterButton
        title={headline}
        value={activeLink ? activeLink.name : undefined}
        isActive={!!activeLink}
        buttonProps={{
          ...toggleProps,
        }}
      />
      {links?.length > 0 && (
        <CollapsibleContent {...collapsibleProps}>
          <ol className="select-links__list">
            {links.map((item: FilterLink) => (
              <li className="select-links__list-item" key={item.name}>
                {isSelectableLink(item) ? (
                  <Hyperlink to={item.url} className="select-links__link">
                    {item.name}
                  </Hyperlink>
                ) : (
                  /* using an anchor here, as this is the current choice but would be a redundant link */
                  /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
                  <a className="select-links__link" aria-disabled="true" aria-current="true" role="link">
                    <span className="select-links__selected-icon">
                      <SVG svgSpriteId={Icons.ACCEPT_CHECK} />
                    </span>
                    <span className="invisible">{t('currentChoice')}: </span>
                    {item.name}
                  </a>
                )}
              </li>
            ))}
          </ol>
        </CollapsibleContent>
      )}
    </div>
  );
}
